/**
 * Equity Accounts Step
 *
 * Fourth step - record owner investments and capital
 */

import { useState, useEffect } from 'react'

import { useBaseCurrency } from '../../hooks/use-currencies'
import { Input, DatePicker } from '../ui'

import type { EquityAccountInput } from '../../types/company-setup'

interface EquityAccountsStepProps {
  equityAccounts: EquityAccountInput[]
  onChange: (accounts: EquityAccountInput[]) => void
  onNext: () => void
  onBack: () => void
}

export function getTodayDateString(): string {
  const n = new Date()
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`
}

export const EMPTY_EQUITY: EquityAccountInput = {
  ownerName: '',
  accountName: '',
  investmentAmount: 0,
  investmentDate: getTodayDateString(),
}

// =============================================================================
// Helpers (exported for tests)
// =============================================================================

export function updateEquityField(
  accounts: EquityAccountInput[],
  index: number,
  field: keyof EquityAccountInput,
  value: string | number,
): EquityAccountInput[] {
  const newAccounts = [...accounts]
  newAccounts[index] = { ...newAccounts[index], [field]: value }

  if (field === 'ownerName' && typeof value === 'string') {
    if (!newAccounts[index].accountName || newAccounts[index].accountName === '') {
      newAccounts[index].accountName = `${value} - Capital`
    }
  }

  return newAccounts
}

export function filterValidAccounts(accounts: EquityAccountInput[]): EquityAccountInput[] {
  return accounts.filter(
    acc => acc.ownerName?.trim() !== '' && acc.accountName?.trim() !== ''
  )
}

// =============================================================================
// Sub-components
// =============================================================================

interface EquityEditFormProps {
  equity: EquityAccountInput
  index: number
  onUpdate: (field: keyof EquityAccountInput, value: string | number) => void
}

function NameFields({ equity, index, onUpdate }: EquityEditFormProps): JSX.Element {
  return (
    <>
      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-1" htmlFor={`equity-${index}-owner-name`}>
          Owner Name <span className="text-red-500">*</span>
        </label>
        <Input
          helperText="Full name of the business owner or partner"
          id={`equity-${index}-owner-name`}
          placeholder="e.g., John Doe"
          type="text"
          value={equity.ownerName}
          onChange={(e): void => onUpdate('ownerName', e.target.value)}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-1" htmlFor={`equity-${index}-account-name`}>
          Capital Account Name <span className="text-red-500">*</span>
        </label>
        <Input
          helperText="This will appear in your chart of accounts"
          id={`equity-${index}-account-name`}
          placeholder="e.g., John Doe - Capital"
          type="text"
          value={equity.accountName}
          onChange={(e): void => onUpdate('accountName', e.target.value)}
        />
      </div>
    </>
  )
}

function InvestmentFields({ equity, index, onUpdate }: EquityEditFormProps): JSX.Element {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 el-gap-field">
      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-1" htmlFor={`equity-${index}-investment-amount`}>
          Investment Amount
        </label>
        <Input
          helperText="Initial capital invested"
          id={`equity-${index}-investment-amount`}
          min="0"
          placeholder="0.00"
          step="0.01"
          type="number"
          value={equity.investmentAmount}
          onChange={(e): void => onUpdate('investmentAmount', parseFloat(e.target.value) || 0)}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground-secondary mb-1" htmlFor={`equity-${index}-investment-date`}>
          Investment Date
        </label>
        <DatePicker
          id={`equity-${index}-investment-date`}
          placeholder="Select investment date"
          value={equity.investmentDate}
          onChange={(date): void => {
            if (date) {
              const year = date.getFullYear()
              const month = String(date.getMonth() + 1).padStart(2, '0')
              const day = String(date.getDate()).padStart(2, '0')
              onUpdate('investmentDate', `${year}-${month}-${day}`)
            } else {
              onUpdate('investmentDate', '')
            }
          }}
        />
      </div>
    </div>
  )
}

function EquityEditForm({ equity, index, onUpdate }: EquityEditFormProps): JSX.Element {
  return (
    <div className="flex flex-col el-gap-field">
      <NameFields equity={equity} index={index} onUpdate={onUpdate} />
      <InvestmentFields equity={equity} index={index} onUpdate={onUpdate} />
    </div>
  )
}

interface EquitySummaryViewProps {
  equity: EquityAccountInput
  baseCurrency: string
}

function EquitySummaryView({ equity, baseCurrency }: EquitySummaryViewProps): JSX.Element {
  return (
    <div className="text-sm text-foreground-secondary space-y-1">
      <p><span className="font-medium">Account:</span> {equity.accountName}</p>
      <p><span className="font-medium">Investment:</span> {baseCurrency} {(equity.investmentAmount ?? 0).toFixed(2)}</p>
      <p><span className="font-medium">Date:</span> {equity.investmentDate?.trim() ? equity.investmentDate : 'N/A'}</p>
    </div>
  )
}

interface EquityCardProps {
  equity: EquityAccountInput
  index: number
  isEditing: boolean
  baseCurrency: string
  onToggleEdit: () => void
  onRemove: () => void
  onUpdate: (field: keyof EquityAccountInput, value: string | number) => void
}

function EquityCard({ equity, index, isEditing, baseCurrency, onToggleEdit, onRemove, onUpdate }: EquityCardProps): JSX.Element {
  return (
    <div className={`border-2 rounded-lg p-4 transition-colors ${isEditing ? 'border-primary bg-muted' : 'border-border'}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-foreground">
          {equity.ownerName?.trim() ? equity.ownerName : `Owner ${index + 1}`}
        </h3>
        <div className="flex items-center gap-2">
          <button className="text-sm text-primary hover:underline" type="button" onClick={onToggleEdit}>
            {isEditing ? 'Collapse' : 'Edit'}
          </button>
          <button className="text-sm text-red-600 dark:text-red-400 hover:underline" type="button" onClick={onRemove}>
            Remove
          </button>
        </div>
      </div>

      {isEditing ? (
        <EquityEditForm equity={equity} index={index} onUpdate={onUpdate} />
      ) : (
        <EquitySummaryView baseCurrency={baseCurrency} equity={equity} />
      )}
    </div>
  )
}

// =============================================================================
// Component
// =============================================================================

function InfoBanner(): JSX.Element {
  return (
    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
      <div className="flex items-start gap-3">
        <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path clipRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" fillRule="evenodd" />
        </svg>
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p className="font-medium mb-1">Record Owner Investments</p>
          <p>Record the initial capital invested by business owners. This creates proper equity accounts and tracks each owner&apos;s investment in the company. You can skip this and add it later if needed.</p>
        </div>
      </div>
    </div>
  )
}

function ExampleBox(): JSX.Element {
  return (
    <div className="p-4 bg-surface-secondary rounded-lg">
      <p className="text-sm font-medium text-foreground-secondary mb-2">Example:</p>
      <ul className="text-sm text-foreground-secondary space-y-1">
        <li>• <strong>Sole Proprietorship:</strong> One owner with their investment</li>
        <li>• <strong>Partnership:</strong> Multiple partners with their respective capital contributions</li>
        <li>• <strong>Private Limited:</strong> Shareholders and their paid-up capital</li>
      </ul>
    </div>
  )
}

interface ActionBarProps {
  onBack: () => void
  onNext: () => void
}

function ActionBar({ onBack, onNext }: ActionBarProps): JSX.Element {
  return (
    <div className="flex justify-between pt-4">
      <button
        className="px-3 py-2 border border-border text-foreground-secondary rounded-lg hover:bg-surface-secondary transition-colors font-medium"
        type="button"
        onClick={onBack}
      >
        Back
      </button>
      <button
        className="px-3 py-2 bg-primary-solid text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
        type="button"
        onClick={onNext}
      >
        Next: Review & Create
      </button>
    </div>
  )
}

interface UseEquityStateReturn {
  editingIndex: number | null
  handleAddEquity: () => void
  handleRemoveEquity: (i: number) => void
  handleUpdateEquity: (index: number, field: keyof EquityAccountInput, value: string | number) => void
  handleToggleEdit: (i: number) => void
}

function useEquityState(equityAccounts: EquityAccountInput[], onChange: (accounts: EquityAccountInput[]) => void): UseEquityStateReturn {
  const shouldAutoExpand =
    equityAccounts.length === 0 ||
    (equityAccounts.length === 1 && (!equityAccounts[0]?.ownerName || equityAccounts[0]?.ownerName.trim() === ''))
  const [editingIndex, setEditingIndex] = useState<number | null>(shouldAutoExpand ? 0 : null)

  useEffect(() => {
    if (equityAccounts.length === 0) { onChange([{ ...EMPTY_EQUITY }]) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    editingIndex,
    handleAddEquity: () => {
      onChange([...equityAccounts, { ...EMPTY_EQUITY }])
      setEditingIndex(equityAccounts.length)
    },
    handleRemoveEquity: (index) => {
      onChange(equityAccounts.filter((_, i) => i !== index))
      setEditingIndex(null)
    },
    handleUpdateEquity: (index, field, value) => {
      onChange(updateEquityField(equityAccounts, index, field, value))
    },
    handleToggleEdit: (index) => setEditingIndex(editingIndex === index ? null : index),
  }
}

interface EquityListProps {
  equityAccounts: EquityAccountInput[]
  editingIndex: number | null
  baseCurrency: string
  onToggleEdit: (i: number) => void
  onRemove: (i: number) => void
  onUpdate: (index: number, field: keyof EquityAccountInput, value: string | number) => void
}

function EquityList({ equityAccounts, editingIndex, baseCurrency, onToggleEdit, onRemove, onUpdate }: EquityListProps): JSX.Element {
  if (equityAccounts.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        <p className="mb-4">No equity accounts added yet.</p>
        <p className="text-sm">Click &quot;Add Owner Investment&quot; below to record initial capital, or skip this step if you prefer to add it later.</p>
      </div>
    )
  }
  return (
    <>
      {equityAccounts.map((equity, index) => (
        <EquityCard
          key={index}
          baseCurrency={baseCurrency}
          equity={equity}
          index={index}
          isEditing={editingIndex === index}
          onRemove={() => onRemove(index)}
          onToggleEdit={() => onToggleEdit(index)}
          onUpdate={(field, value) => onUpdate(index, field, value)}
        />
      ))}
    </>
  )
}

export function EquityAccountsStep({ equityAccounts, onChange, onNext, onBack }: EquityAccountsStepProps): JSX.Element {
  const baseCurrency = useBaseCurrency()
  const { editingIndex, handleAddEquity, handleRemoveEquity, handleUpdateEquity, handleToggleEdit } =
    useEquityState(equityAccounts, onChange)

  const handleNext = (): void => {
    onChange(filterValidAccounts(equityAccounts))
    void setTimeout(() => onNext(), 0)
  }

  return (
    <div className="space-y-6">
      <InfoBanner />
      <div className="space-y-4">
        <EquityList
          baseCurrency={baseCurrency}
          editingIndex={editingIndex}
          equityAccounts={equityAccounts}
          onRemove={handleRemoveEquity}
          onToggleEdit={handleToggleEdit}
          onUpdate={handleUpdateEquity}
        />
      </div>
      <button
        className="w-full py-3 border-2 border-dashed border-border rounded-lg text-foreground-secondary hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2"
        type="button"
        onClick={handleAddEquity}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 4v16m8-8H4" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
        </svg>
        {equityAccounts.length === 0 ? 'Add Owner Investment' : 'Add Another Owner'}
      </button>
      <ExampleBox />
      <ActionBar onBack={onBack} onNext={handleNext} />
    </div>
  )
}
